import { useEffect, useMemo, useState } from "react";
import { Factory, History, LogOut, PanelLeftClose, PanelLeftOpen, ShieldAlert, Sparkles, TrendingUp, User, UserCircle2, Users } from "lucide-react";
import LifecycleOverlay from "../components/LifecycleOverlay";
import { AskPanel } from "../components/AskPanel";
import { EmployersPanel } from "../components/EmployersPanel";
import SalesSidebar from "../components/SalesSidebar";
import SalesMap from "../components/SalesMap";
import ContactDialog from "../components/ContactDialog";
import FirmDialog from "../components/FirmDialog";
import UsersDialog from "../components/UsersDialog";
import SecurityDialog from "../components/SecurityDialog";
import ActivityDialog from "../components/ActivityDialog";
import AccountDialog from "../components/AccountDialog";
import ChangePassword from "./ChangePassword";
import ConnectionPickerDialog from "../components/ConnectionPickerDialog";
import { TIERS, TIER_ORDER, TERRITORIES, SALES_TEAM_KIND } from "../data/broker";
import { industryOf } from "../data/employer";
import { useContacts } from "../hooks/useContacts";
import { useFirms } from "../hooks/useFirms";
import { useConnections } from "../hooks/useConnections";
import { useAbmScope } from "../hooks/useAbmScope";
import { useTpaNetworks } from "../hooks/useTpaNetworks";
import { useMapVisible } from "../hooks/useMapVisible";
import { useAuth } from "../auth/AuthContext";
import { API } from "../auth/api";

const ALL_TIERS = Object.fromEntries(TIER_ORDER.map((k) => [k, true]));

export default function Landing() {
  const [activeTier, setActiveTier] = useState("all");
  const [activeTerritory, setActiveTerritory] = useState("all");
  const [showStates, setShowStates] = useState(true);
  const [choropleth, setChoropleth] = useState(true);
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [lifecycleState, setLifecycleState] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [focusedFirmId, setFocusedFirmId] = useState(null);
  const [query, setQuery] = useState("");
  const [hoveredState, setHoveredState] = useState(null);
  const [activeTab, setActiveTab] = useState("contacts");
  const [pendingContactCoords, setPendingContactCoords] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false); // TPA / sales-team create path
  const [newContactSeed, setNewContactSeed] = useState(null); // prefill for new-contact create
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingFirmId, setEditingFirmId] = useState(null);
  // Broker contacts and firms are sourced from HubSpot — there is no in-app
  // create path for them. TPA directory entries and sales-team members are
  // app-owned and still created here.
  const [showUsers, setShowUsers] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [openAlerts, setOpenAlerts] = useState(0);
  const [showAccount, setShowAccount] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [connectSourceId, setConnectSourceId] = useState(null);
  const [showConnectionsPanel, setShowConnectionsPanel] = useState(false);
  // When a Signals suggestion is opened, isolate the map to just those two.
  const [isolatedIds, setIsolatedIds] = useState(null); // Set<string> | null

  const { contacts, addContact, updateContact, deleteContact, refetch: refetchContacts } = useContacts();
  const { firms, updateFirm, deleteFirm } = useFirms();
  const { connections, addConnection, removeConnection, completeConnection, uncompleteConnection } = useConnections();
  const { scope, setScope } = useAbmScope();
  // TPA × State coverage — union across every region so any territory can
  // be shaded from a single data source.
  const tpaData = useTpaNetworks();
  const [selectedTpa, setSelectedTpa] = useState("all"); // "all" or a TPA display name
  const [focusedNetworkState, setFocusedNetworkState] = useState(null); // "AL" | "NC" | ...
  // Toggle to hide contact markers while studying the networks overlay.
  // Independent map control: hide/show contact markers on any tab.
  const [showContactMarkers, setShowContactMarkers] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  // Toggle to show TPA point-of-contact markers on the map (indigo).
  const [showTpaContactsOnMap, setShowTpaContactsOnMap] = useState(false);
  // Employers overlay: toggled from the map control stack (button, like the
  // contact-marker and lifecycle toggles), not a sidebar tab.
  const [showEmployers, setShowEmployers] = useState(false);
  // Collapse the left navigation column to hand the full width to the map.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [industryFilter, setIndustryFilter] = useState(null);

  // Both overlays live in the same right-hand rail, so opening one closes the
  // other instead of stacking them on top of each other.
  const toggleLifecycle = () => {
    const next = !showLifecycle;
    setShowLifecycle(next);
    if (next) setShowEmployers(false);
  };
  const toggleEmployers = () => {
    const next = !showEmployers;
    setShowEmployers(next);
    if (next) setShowLifecycle(false);
  };
  // Which TPA has its Directory row expanded in the sidebar.
  const [expandedTpaName, setExpandedTpaName] = useState(null);
  const { logout, user, isAdmin } = useAuth();
  const mapVisible = useMapVisible();

  // Split contacts into buckets. Sales team gets its own view / map rule.
  // TPA contacts are relationship-management (Networks Directory), never
  // part of ABM/tier/territory analytics.
  const crmContactsAll = useMemo(
    () =>
      contacts.filter(
        (c) => c.kind !== SALES_TEAM_KIND && c.kind !== "tpa_contact",
      ),
    [contacts],
  );
  const salesTeam = useMemo(
    () => contacts.filter((c) => c.kind === SALES_TEAM_KIND),
    [contacts],
  );
  const tpaContacts = useMemo(
    () => contacts.filter((c) => c.kind === "tpa_contact"),
    [contacts],
  );
  // Employers come from HubSpot companies (Customer stage), not contacts.
  const [employers, setEmployers] = useState([]);
  useEffect(() => {
    let alive = true;
    API.get("/employers")
      .then(({ data }) => {
        if (alive) setEmployers(data || []);
      })
      .catch((e) => console.error("Failed to load employers", e));
    return () => {
      alive = false;
    };
  }, []);

  // Apply the ABM / All Data scope. Sales team is never affected by scope
  // (they're always non-ABM but their visibility is governed by the Sales
  // Team tab / selection logic).
  const crmContacts = useMemo(() => {
    if (scope === "all") return crmContactsAll;
    return crmContactsAll.filter((c) => c.is_abm !== false);
  }, [crmContactsAll, scope]);

  const scopedFirms = useMemo(() => {
    if (scope === "all") return firms;
    return firms.filter((f) => f.is_abm !== false);
  }, [firms, scope]);

  // When the scope changes, drop any selection/isolation that no longer
  // belongs to the visible dataset so the map/sidebar don't hold stale refs.
  const scopedContactIds = useMemo(
    () => new Set([...crmContacts.map((c) => c.id), ...salesTeam.map((c) => c.id)]),
    [crmContacts, salesTeam],
  );
  useEffect(() => {
    if (selectedId && !scopedContactIds.has(selectedId)) {
      setSelectedId(null);
      setShowConnectionsPanel(false);
    }
    if (focusedFirmId && !scopedFirms.some((f) => f.id === focusedFirmId)) {
      setFocusedFirmId(null);
    }
    if (isolatedIds) {
      const filtered = Array.from(isolatedIds).filter((id) => scopedContactIds.has(id));
      if (filtered.length === 0) setIsolatedIds(null);
      else if (filtered.length !== isolatedIds.size) setIsolatedIds(new Set(filtered));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopedContactIds, scopedFirms]);

  // Contacts with no geocoded position have no marker to open, so say so
  // instead of the row click appearing to do nothing.
  const [noMapHint, setNoMapHint] = useState("");
  useEffect(() => {
    if (!selectedId) return undefined;
    const c = [...crmContacts, ...salesTeam].find((x) => x.id === selectedId);
    if (c && !c.coords) {
      setNoMapHint("This contact has no map location yet — open it from Edit to add a city or state.");
      const t = setTimeout(() => setNoMapHint(""), 4500);
      return () => clearTimeout(t);
    }
    setNoMapHint("");
    return undefined;
  }, [selectedId, crmContacts, salesTeam]);

  // Poll the open-alert count so an admin sees a badge without opening the panel.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await API.get("/security/summary");
        if (!cancelled) setOpenAlerts(data.open_alerts || 0);
      } catch {
        /* badge is best-effort */
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin, showSecurity]);

  const salesTeamIds = useMemo(
    () => new Set(salesTeam.map((c) => c.id)),
    [salesTeam],
  );

  // Scoped set of all contacts (CRM + sales-team) — the map should never
  // see non-ABM CRM records when scope=abm.
  const scopedAll = useMemo(
    () => [...crmContacts, ...salesTeam],
    [crmContacts, salesTeam],
  );

  // Sales team nodes only stay on the map when the Sales Team tab is
  // active, OR when a sales-team member is currently selected/isolated —
  // otherwise the map declutters back to CRM contacts only.
  const activeSalesTeamIds = useMemo(() => {
    const showByTab = activeTab === "sales-team";
    const showBySelection = selectedId && salesTeamIds.has(selectedId);
    const showByIsolation =
      isolatedIds && Array.from(isolatedIds).some((id) => salesTeamIds.has(id));
    if (showByTab || showBySelection || showByIsolation) return salesTeamIds;
    return new Set();
  }, [activeTab, selectedId, isolatedIds, salesTeamIds]);

  const visibleTiers = useMemo(() => {
    if (activeTier === "all") return ALL_TIERS;
    return Object.fromEntries(TIER_ORDER.map((k) => [k, k === activeTier]));
  }, [activeTier]);

  const editingContact = useMemo(
    () => (editingContactId ? contacts.find((c) => c.id === editingContactId) : null),
    [editingContactId, contacts],
  );
  const editingFirm = useMemo(
    () => (editingFirmId ? firms.find((f) => f.id === editingFirmId) : null),
    [editingFirmId, firms],
  );
  const focused = useMemo(
    () => (selectedId ? contacts.find((c) => c.id === selectedId) || null : null),
    [selectedId, contacts],
  );
  const connectSource = useMemo(
    () => (connectSourceId ? contacts.find((c) => c.id === connectSourceId) || null : null),
    [connectSourceId, contacts],
  );

  const choroplethTint = useMemo(
    () => (activeTier === "all" ? "#22D3EE" : TIERS[activeTier].color),
    [activeTier],
  );

  const stateCounts = useMemo(() => {
    const counts = {};
    for (const c of crmContacts) {
      if (!visibleTiers[c.tier]) continue;
      if (activeTerritory !== "all" && c.territory !== activeTerritory) continue;
      if (focusedFirmId && c.firm_id !== focusedFirmId) continue;
      if (!c.state) continue;
      counts[c.state] = (counts[c.state] || 0) + 1;
    }
    return counts;
  }, [crmContacts, visibleTiers, activeTerritory, focusedFirmId]);

  // Whether the map should render the TPA/network overlay instead of the
  // contact-count choropleth. Active while the Networks tab is open.
  const networksMode = activeTab === "networks";

  // Employers mode: the map switches to employer markers coloured by industry
  // and shades states by employer density.
  const employersMode = showEmployers;
  const visibleEmployers = useMemo(() => {
    if (!employersMode) return [];
    if (!industryFilter || industryFilter.size === 0) return employers;
    return employers.filter((e) => industryFilter.has(industryOf(e)));
  }, [employers, employersMode, industryFilter]);

  const employerStateCounts = useMemo(() => {
    const counts = {};
    for (const e of visibleEmployers) {
      if (!e.state) continue;
      counts[e.state] = (counts[e.state] || 0) + 1;
    }
    return counts;
  }, [visibleEmployers]);

  // Map region key -> territory key (they match: southeast/northeast/midwest/south).
  // Filter tpa coverage to states in the current territory when set.
  const scopedTpaCoverage = useMemo(() => {
    const src = tpaData.coverageByState || {};
    if (activeTerritory === "all") return src;
    const map = tpaData.state_to_region || {};
    const out = {};
    for (const [state, info] of Object.entries(src)) {
      if (map[state] === activeTerritory) out[state] = info;
    }
    return out;
  }, [tpaData.coverageByState, tpaData.state_to_region, activeTerritory]);

  // Per-state coverage rendered on the map when in networksMode.
  //   - If a TPA is selected: value = number of carriers that TPA offers there
  //   - Else: value = total unique carriers across all TPAs in that state
  const networkStateCounts = useMemo(() => {
    if (!networksMode) return {};
    const counts = {};
    for (const [state, info] of Object.entries(scopedTpaCoverage || {})) {
      if (selectedTpa === "all") {
        counts[state] = info.carrierCount || 0;
      } else {
        const entry = (info.tpas || []).find((t) => t.tpa === selectedTpa);
        counts[state] = entry ? entry.carriers.length : 0;
      }
    }
    return counts;
  }, [networksMode, scopedTpaCoverage, selectedTpa]);


  // Per-contact edge count (for popup badge + sidebar row indicator).
  const contactConnectionCount = useMemo(() => {
    const m = new Map();
    for (const e of connections) {
      m.set(e.contact_a, (m.get(e.contact_a) || 0) + 1);
      m.set(e.contact_b, (m.get(e.contact_b) || 0) + 1);
    }
    return m;
  }, [connections]);

  // Peers of the currently selected contact (for the sidebar connections panel).
  const selectedPeers = useMemo(() => {
    if (!selectedId) return [];
    const contactsById = new Map(contacts.map((c) => [c.id, c]));
    return connections
      .filter((e) => e.contact_a === selectedId || e.contact_b === selectedId)
      .map((e) => {
        const peerId = e.contact_a === selectedId ? e.contact_b : e.contact_a;
        return { connection: e, peer: contactsById.get(peerId) };
      })
      .filter((row) => Boolean(row.peer));
  }, [selectedId, connections, contacts]);

  const existingPeerIds = useMemo(() => {
    if (!connectSourceId) return new Set();
    const s = new Set();
    for (const e of connections) {
      if (e.contact_a === connectSourceId) s.add(e.contact_b);
      else if (e.contact_b === connectSourceId) s.add(e.contact_a);
    }
    return s;
  }, [connectSourceId, connections]);

  return (
    <div
      className="h-screen w-screen flex flex-col bg-[#09090B] text-zinc-100 overflow-hidden grid-bg"
      data-testid="landing-root"
    >
      <header
        className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-[#0a0a0c] shrink-0"
        data-testid="topbar"
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <img
              src="/virtue-logo.png"
              alt=""
              className="h-5 w-auto shrink-0 block"
              data-testid="topbar-logo"
            />
            <span className="font-mono text-[11px] tracking-[0.28em] text-zinc-200 uppercase whitespace-nowrap">
              Virtue Health
            </span>
            <span className="hidden xl:inline font-mono text-[10px] text-zinc-600 ml-2 whitespace-nowrap">
              / ACCOUNT NEURAL NETWORK
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-5 font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">
          <span className="hidden xl:inline whitespace-nowrap">
            <span className="text-zinc-300">{scopedFirms.length}</span> FIRMS ·{" "}
            <span className="text-zinc-300">{crmContacts.length}</span> CONTACTS ·{" "}
            <span className="text-zinc-300">{salesTeam.length}</span> SALES ·{" "}
            <span className="text-zinc-300">{connections.length}</span> LINKS
          </span>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowActivity(true)}
              data-testid="team-activity-button"
              aria-label="Team activity"
              className="flex items-center gap-1.5 border border-white/10 hover:border-cyan-400/60 hover:text-cyan-300 px-2.5 py-1 text-zinc-400 transition-colors"
              title="Who changed what this week"
            >
              <History className="w-3 h-3" />
              <span className="hidden sm:inline tracking-[0.18em]">ACTIVITY</span>
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowSecurity(true)}
              data-testid="export-alerts-button"
              aria-label={
                openAlerts > 0 ? `Export alerts, ${openAlerts} open` : "Export alerts"
              }
              className={`relative flex items-center gap-1.5 border px-2.5 py-1 transition-colors ${
                openAlerts > 0
                  ? "border-amber-400/60 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20"
                  : "border-white/10 text-zinc-400 hover:border-cyan-400/60 hover:text-cyan-300"
              }`}
              title="Unusual export / bulk read alerts"
            >
              <ShieldAlert className="w-3 h-3" />
              <span className="hidden sm:inline tracking-[0.18em]">ALERTS</span>
              {openAlerts > 0 && (
                <span
                  className="ml-0.5 px-1 border border-amber-400/60 bg-[#0a0a0c] font-mono text-[9px] text-amber-200"
                  data-testid="alerts-badge"
                >
                  {openAlerts}
                </span>
              )}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setShowUsers(true)}
              data-testid="manage-users-button"
              aria-label="Manage team accounts"
              className="flex items-center gap-1.5 border border-white/10 hover:border-cyan-400/60 hover:text-cyan-300 px-2.5 py-1 text-zinc-400 transition-colors"
              title="Manage team accounts"
            >
              <Users className="w-3 h-3" />
              <span className="hidden sm:inline tracking-[0.18em]">TEAM</span>
            </button>
          )}
          {user && (
            <button
              type="button"
              onClick={() => setShowAccount(true)}
              data-testid="account-chip"
              aria-label="My account, sessions and password"
              className="flex items-center gap-1.5 border border-white/10 hover:border-cyan-400/60 hover:text-cyan-300 px-2.5 py-1 text-zinc-400 transition-colors"
              title="My account, sessions and password"
            >
              <UserCircle2 className="w-3 h-3" />
              <span className="hidden sm:inline tracking-[0.18em] normal-case text-zinc-300 max-w-[10rem] truncate">
                {user.name}
              </span>
            </button>
          )}
          <span className="hidden md:inline">
            <span className="text-emerald-400">●</span> ONLINE
          </span>
          <button
            type="button"
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-1.5 border border-white/10 hover:border-rose-400/60 hover:text-rose-300 px-2.5 py-1 text-zinc-400 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-3 h-3" />
            <span className="hidden sm:inline tracking-[0.18em]">LOGOUT</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {mapVisible && (
          <div
            className="w-9 shrink-0 flex flex-col items-center border-r border-white/10 bg-[#0a0a0c] py-3"
            data-testid="sidebar-rail"
          >
            <button
              type="button"
              onClick={() => setSidebarCollapsed((v) => !v)}
              data-testid="toggle-sidebar"
              aria-pressed={sidebarCollapsed}
              aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
              title={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
              className="w-7 h-7 flex items-center justify-center border border-white/10 text-zinc-400 hover:border-cyan-400/60 hover:text-cyan-300 transition-colors"
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="w-4 h-4" strokeWidth={1.75} />
              ) : (
                <PanelLeftClose className="w-4 h-4" strokeWidth={1.75} />
              )}
            </button>
          </div>
        )}
        {(!sidebarCollapsed || !mapVisible) && (
        <SalesSidebar
          contacts={crmContacts}
          salesTeam={salesTeam}
          firms={scopedFirms}
          connections={connections}
          scope={scope}
          setScope={setScope}
          abmContactTotal={crmContactsAll.filter((c) => c.is_abm !== false).length}
          allContactTotal={crmContactsAll.length}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeTier={activeTier}
          setActiveTier={(t) => {
            setActiveTier(t);
            setFocusedFirmId(null);
            setIsolatedIds(null);
          }}
          activeTerritory={activeTerritory}
          setActiveTerritory={(t) => {
            setActiveTerritory(t);
            setFocusedFirmId(null);
            setIsolatedIds(null);
          }}
          visibleTiers={visibleTiers}
          showStates={showStates}
          setShowStates={setShowStates}
          choropleth={choropleth}
          setChoropleth={setChoropleth}
          selectedId={selectedId}
          onSelect={setSelectedId}
          hoveredState={hoveredState}
          query={query}
          setQuery={setQuery}
          focusedFirmId={focusedFirmId}
          setFocusedFirmId={setFocusedFirmId}
          onEditFirm={(id) => setEditingFirmId(id)}
          onDeleteFirm={async (id) => {
            if (!window.confirm("Delete this firm from the list?")) return;
            await deleteFirm(id);
          }}
          onEditContact={(id) => setEditingContactId(id)}
          contactConnectionCount={contactConnectionCount}
          selectedPeers={selectedPeers}
          selectedContact={focused}
          showConnectionsPanel={showConnectionsPanel}
          onShowConnectionsPanel={(v) => setShowConnectionsPanel(v)}
          onStartConnect={(id) => setConnectSourceId(id)}
          onRemoveConnection={async (connId) => {
            if (!window.confirm("Remove this connection?")) return;
            await removeConnection(connId);
          }}
          onOpenSuggestion={(s) => {
            // Isolate the map to just the warm & cold contacts for this play.
            setFocusedFirmId(null);
            setIsolatedIds(new Set([s.warm.id, s.cold.id]));
            setSelectedId(s.warm.id);
            setShowConnectionsPanel(true);
          }}
          onCompleteSignal={async (id) => {
            await completeConnection(id);
          }}
          onUncompleteSignal={async (id) => {
            await uncompleteConnection(id);
          }}
          isolatedIds={isolatedIds}
          onClearIsolation={() => setIsolatedIds(null)}
          // Networks tab
          tpaData={tpaData}
          scopedTpaCoverage={scopedTpaCoverage}
          selectedTpa={selectedTpa}
          setSelectedTpa={setSelectedTpa}
          focusedNetworkState={focusedNetworkState}
          setFocusedNetworkState={setFocusedNetworkState}
          showTpaContactsOnMap={showTpaContactsOnMap}
          setShowTpaContactsOnMap={setShowTpaContactsOnMap}
          tpaContacts={tpaContacts}
          expandedTpaName={expandedTpaName}
          setExpandedTpaName={setExpandedTpaName}
          onEditTpaContact={(id) => setEditingContactId(id)}
          onDeleteTpaContact={async (id) => {
            if (!window.confirm("Remove this TPA contact?")) return;
            await deleteContact(id);
          }}
          onAddTpaContact={(tpaName) => {
            setNewContactSeed({
              kind: "tpa_contact",
              tier: "tpa",
              tpa_name: tpaName || "",
              company_name: tpaName || "",
            });
            setContactDialogOpen(true);
          }}
          onImportTpaContactsCsv={async (file) => {
            const fd = new FormData();
            fd.append("file", file);
            try {
              const { data } = await API.post("/tpa-contacts/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
              const skippedCount = (data.skipped || []).length;
              const erroredCount = (data.errored || []).length;
              window.alert(
                `Imported ${data.inserted} TPA contact${data.inserted === 1 ? "" : "s"}.` +
                  (skippedCount ? ` Skipped ${skippedCount}.` : "") +
                  (erroredCount ? ` Errored ${erroredCount}.` : ""),
              );
              // Refresh contacts list
              if (typeof refetchContacts === "function") refetchContacts();
              else window.location.reload();
            } catch (e) {
              window.alert("Import failed: " + (e?.response?.data?.detail || e.message));
            }
          }}
        />
        )}
        {mapVisible && (
          <main className="flex-1 relative min-w-0" data-testid="map-pane">
          <button
            type="button"
            onClick={() => setShowContactMarkers((v) => !v)}
            data-testid="toggle-contact-markers"
            aria-pressed={showContactMarkers}
            aria-label={showContactMarkers ? "Hide contact markers" : "Show contact markers"}
            title={showContactMarkers ? "Hide contact markers" : "Show contact markers"}
            className={`absolute z-[800] top-4 right-4 w-11 h-11 flex items-center justify-center border backdrop-blur-sm transition-colors ${
              showContactMarkers
                ? "border-white/15 bg-[#0a0a0c]/85 text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-300"
                : "border-emerald-400/60 bg-emerald-500/15 text-emerald-100"
            }`}
          >
            <span className="relative flex items-center justify-center">
              <User className="w-5 h-5" strokeWidth={1.75} />
              {!showContactMarkers && (
                <span
                  aria-hidden="true"
                  className="absolute w-[26px] h-[1.5px] bg-emerald-200 rotate-45 rounded-full"
                />
              )}
            </span>
          </button>
          <button
            type="button"
            onClick={toggleLifecycle}
            data-testid="toggle-lifecycle-metrics"
            aria-pressed={showLifecycle}
            aria-label={showLifecycle ? "Hide lifecycle metrics" : "Show lifecycle metrics"}
            title={showLifecycle ? "Hide lifecycle metrics" : "Lifecycle stage progression"}
            className={`absolute z-[800] top-[68px] right-4 w-11 h-11 flex items-center justify-center border backdrop-blur-sm transition-colors ${
              showLifecycle
                ? "border-cyan-400/60 bg-cyan-500/15 text-cyan-100"
                : "border-white/15 bg-[#0a0a0c]/85 text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-300"
            }`}
          >
            <TrendingUp className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {showLifecycle && (
            <LifecycleOverlay
              state={lifecycleState}
              isAdmin={user?.role === "admin"}
              onClose={() => setShowLifecycle(false)}
              onClearState={() => setLifecycleState(null)}
            />
          )}
          <button
            type="button"
            onClick={toggleEmployers}
            data-testid="toggle-employers"
            aria-pressed={showEmployers}
            aria-label={showEmployers ? "Hide employers" : "Show employers"}
            title={showEmployers ? "Hide employers" : "Employer customers by industry"}
            className={`absolute z-[800] top-[132px] right-4 w-11 h-11 flex items-center justify-center border backdrop-blur-sm transition-colors ${
              showEmployers
                ? "border-amber-400/60 bg-amber-500/15 text-amber-100"
                : "border-white/15 bg-[#0a0a0c]/85 text-zinc-300 hover:border-amber-400/60 hover:text-amber-300"
            }`}
          >
            <Factory className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {showEmployers && (
            <EmployersPanel
              employers={employers}
              industryFilter={industryFilter}
              setIndustryFilter={setIndustryFilter}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId(id)}
              onClose={() => setShowEmployers(false)}
            />
          )}
          <button
            type="button"
            onClick={() => setShowAsk((v) => !v)}
            data-testid="toggle-ask"
            aria-pressed={showAsk}
            aria-label={showAsk ? "Close search" : "Ask about contacts and meetings"}
            title={showAsk ? "Close search" : "Ask about contacts and meetings"}
            className={`absolute z-[800] top-[196px] right-4 w-11 h-11 flex items-center justify-center border backdrop-blur-sm transition-colors ${
              showAsk
                ? "border-violet-400/60 bg-violet-500/15 text-violet-100"
                : "border-white/15 bg-[#0a0a0c]/85 text-zinc-300 hover:border-violet-400/60 hover:text-violet-300"
            }`}
          >
            <Sparkles className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {showAsk && <AskPanel onClose={() => setShowAsk(false)} />}
          <SalesMap
            contacts={
              !showContactMarkers
                ? [
                    ...(showTpaContactsOnMap
                      ? tpaContacts.filter((c) => c.coords)
                      : []),
                    ...visibleEmployers.filter((c) => c.coords),
                  ]
                : [
                    ...(activeSalesTeamIds.size > 0 ? scopedAll : crmContacts),
                    ...(showTpaContactsOnMap
                      ? tpaContacts.filter((c) => c.coords)
                      : []),
                    ...visibleEmployers.filter((c) => c.coords),
                  ]
            }
            salesTeamIds={activeSalesTeamIds}
            connections={connections}
            visibleTiers={visibleTiers}
            activeTerritory={activeTerritory}
            showStates={showStates}
            choropleth={choropleth || networksMode || employersMode}
            choroplethTint={
              networksMode ? "#22C55E" : employersMode ? "#F59E0B" : choroplethTint
            }
            stateCounts={
              networksMode
                ? networkStateCounts
                : employersMode
                  ? employerStateCounts
                  : stateCounts
            }
            selectedId={selectedId}
            onSelect={setSelectedId}
            focused={focused}
            onStateHover={setHoveredState}
            hoveredState={hoveredState}
            focusedFirmId={focusedFirmId}
            isolatedIds={isolatedIds}
            // Networks-mode overlay context
            networksMode={networksMode}
            lifecycleMode={showLifecycle}
            onLifecycleStateSelect={setLifecycleState}
            lifecycleState={lifecycleState}
            selectedTpa={selectedTpa}
            tpaCoverageByState={tpaData.coverageByState}
            stateMarketTiers={tpaData.state_market_tiers}
            onNetworkStateSelect={setFocusedNetworkState}
            focusedNetworkState={focusedNetworkState}
            addMode={false}
            onMapPick={(coords) => setPendingContactCoords(coords)}
            onEditContact={(id) => setEditingContactId(id)}
            onDeleteContact={async (id) => {
              await deleteContact(id);
              if (selectedId === id) setSelectedId(null);
            }}
            onAddConnection={(id) => setConnectSourceId(id)}
            onViewConnections={(id) => {
              setSelectedId(id);
              setShowConnectionsPanel(true);
            }}
            contactConnectionCount={contactConnectionCount}
          />
        </main>
        )}
      </div>

      {!mapVisible && (
        <>
          <button
            type="button"
            onClick={toggleLifecycle}
            data-testid="toggle-lifecycle-metrics-mobile"
            aria-pressed={showLifecycle}
            aria-label={showLifecycle ? "Hide lifecycle metrics" : "Show lifecycle metrics"}
            className={`fixed z-[1600] bottom-16 right-4 w-12 h-12 flex items-center justify-center border backdrop-blur-sm ${
              showLifecycle
                ? "border-cyan-400/60 bg-cyan-500/20 text-cyan-100"
                : "border-white/15 bg-[#0a0a0c]/95 text-zinc-300"
            }`}
          >
            <TrendingUp className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={toggleEmployers}
            data-testid="toggle-employers-mobile"
            aria-pressed={showEmployers}
            aria-label={showEmployers ? "Hide employers" : "Show employers"}
            className={`fixed z-[1600] bottom-[124px] right-4 w-12 h-12 flex items-center justify-center border backdrop-blur-sm ${
              showEmployers
                ? "border-amber-400/60 bg-amber-500/20 text-amber-100"
                : "border-white/15 bg-[#0a0a0c]/95 text-zinc-300"
            }`}
          >
            <Factory className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={() => setShowAsk((v) => !v)}
            data-testid="toggle-ask-mobile"
            aria-pressed={showAsk}
            aria-label={showAsk ? "Close search" : "Ask about contacts and meetings"}
            className={`fixed z-[1600] bottom-[188px] right-4 w-12 h-12 flex items-center justify-center border backdrop-blur-sm ${
              showAsk
                ? "border-violet-400/60 bg-violet-500/20 text-violet-100"
                : "border-white/15 bg-[#0a0a0c]/95 text-zinc-300"
            }`}
          >
            <Sparkles className="w-5 h-5" strokeWidth={1.75} />
          </button>
          {showAsk && (
            <div className="fixed inset-x-2 top-14 bottom-2 z-[1620]">
              <AskPanel onClose={() => setShowAsk(false)} mobile />
            </div>
          )}
          {showEmployers && (
            <div className="fixed inset-0 z-[1590] pointer-events-none">
              <div className="absolute inset-0 pointer-events-auto">
                <EmployersPanel
                  employers={employers}
                  industryFilter={industryFilter}
                  setIndustryFilter={setIndustryFilter}
                  selectedId={selectedId}
                  onSelect={(id) => setSelectedId(id)}
                  onClose={() => setShowEmployers(false)}
                />
              </div>
            </div>
          )}
          {showLifecycle && (
            <div className="fixed inset-0 z-[1590] pointer-events-none">
              <div className="absolute inset-0 pointer-events-auto">
                <LifecycleOverlay
                  state={null}
                  isAdmin={user?.role === "admin"}
                  onClose={() => setShowLifecycle(false)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {noMapHint && (
        <div
          className="fixed z-[1500] left-1/2 -translate-x-1/2 bottom-6 max-w-[min(92%,26rem)] border border-amber-400/40 bg-[#0a0a0c]/95 px-3 py-2 font-mono text-[10px] leading-relaxed tracking-[0.12em] uppercase text-amber-200"
          data-testid="no-map-location-hint"
        >
          {noMapHint}
        </div>
      )}

      <ContactDialog
        open={
          Boolean(pendingContactCoords) || Boolean(editingContact) || contactDialogOpen
        }
        coords={pendingContactCoords}
        initial={editingContact || newContactSeed || undefined}
        allTpas={tpaData.tpas || []}
        onCancel={() => {
          setPendingContactCoords(null);
          setEditingContactId(null);
          setContactDialogOpen(false);
          setNewContactSeed(null);
        }}
        onSave={async (payload) => {
          if (editingContact) {
            await updateContact(editingContact.id, payload);
            setEditingContactId(null);
          } else {
            const coords = pendingContactCoords || payload.coords || null;
            const created = await addContact({ ...payload, coords });
            setPendingContactCoords(null);
            setContactDialogOpen(false);
            setNewContactSeed(null);
            if (created?.id) setSelectedId(created.id);
          }
        }}
      />

      <FirmDialog
        open={Boolean(editingFirm)}
        initial={editingFirm || undefined}
        onCancel={() => {
          setEditingFirmId(null);
        }}
        onSave={async (payload) => {
          if (editingFirm) {
            await updateFirm(editingFirm.id, payload);
            setEditingFirmId(null);
          }
        }}
      />

      <ConnectionPickerDialog
        open={Boolean(connectSource)}
        sourceContact={connectSource}
        contacts={contacts}
        existingPeerIds={existingPeerIds}
        onCancel={() => setConnectSourceId(null)}
        onPick={async (peer) => {
          if (!connectSource) return;
          await addConnection(connectSource.id, peer.id);
          // Reveal the network right away
          setSelectedId(connectSource.id);
          setShowConnectionsPanel(true);
        }}
      />

      <UsersDialog
        open={showUsers}
        currentUser={user}
        onClose={() => setShowUsers(false)}
      />

      <SecurityDialog open={showSecurity} onClose={() => setShowSecurity(false)} />

      <ActivityDialog open={showActivity} onClose={() => setShowActivity(false)} />

      <AccountDialog
        open={showAccount}
        onClose={() => setShowAccount(false)}
        onChangePassword={() => {
          setShowAccount(false);
          setShowChangePassword(true);
        }}
      />

      {showChangePassword && (
        <div className="fixed inset-0 z-[2100]" data-testid="change-password-overlay">
          <ChangePassword
            onDone={() => setShowChangePassword(false)}
            onCancel={() => setShowChangePassword(false)}
          />
        </div>
      )}

      <span data-territories={Object.keys(TERRITORIES).join(",")} className="hidden" />
    </div>
  );
}
